import { useState } from "react";
import { firestore, storage } from "../firebaseConfig";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { ref, uploadString, getDownloadURL } from "firebase/storage";
import { toast } from "react-toastify";
import { Camera } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TowClampCreate = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    type: "Clamp",
    vehicleType: "2W",
    vehicleNumber: "",
    reason: "",
    yard: "",
    amount: "",
    vehicleImages: [],
    numberPlateImage: "",
    location: null,
  });

  const [loading, setLoading] = useState(false);
const captureImage = (field, multiple = false) => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.capture = "environment";

  input.onchange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;

      setForm((prev) => ({
        ...prev,
        [field]: multiple
          ? [...prev.vehicleImages, base64]
          : base64,
      }));
    };

    reader.readAsDataURL(file);
  };

  input.click();
};
  const uploadImage = async (base64) => {
    const storageRef = ref(
        storage,
        `tow-clamp/${Date.now()}-${Math.random()}.jpg`
    );

    await uploadString(storageRef, base64, "data_url");
    return await getDownloadURL(storageRef);
    };
const handleSubmit = async () => {
  try {
    if (!form.vehicleNumber) {
      toast.error("Enter vehicle number");
      return;
    }

    setLoading(true);

    let vehicleImageUrls = [];
    if (form.vehicleImages.length > 0) {
      vehicleImageUrls = await Promise.all(
        form.vehicleImages.map((img) => uploadImage(img))
      );
    }

    let numberPlateUrl = "";
    if (form.numberPlateImage) {
      numberPlateUrl = await uploadImage(form.numberPlateImage);
    }

    const location = await new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) =>
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            }),
          () => resolve("Location access denied")
        );
      } else {
        resolve("Geolocation not supported");
      }
    });

    await addDoc(collection(firestore, "tow_clamp_collection"), {
      ...form,
      vehicleImages: vehicleImageUrls, // ✅ URLs
      numberPlateImage: numberPlateUrl, // ✅ URL
      location,
      createdAt: Timestamp.now(),
    });

    toast.success("Saved successfully 🚀");
    navigate('/tow-clamp')
    // Reset form
    setForm({
      type: "Clamp",
      vehicleType: "2W",
      vehicleNumber: "",
      reason: "",
      yard: "",
      amount: "",
      vehicleImages: [],
      numberPlateImage: "",
      location: null,
    });

  } catch (err) {
    console.error(err);
    toast.error("Error saving data");
  } finally {
    setLoading(false);
  }
};

  return (
    <div style={styles.container}>
      <h2>Create Tow / Clamp</h2>

      {/* Type */}
      <div style={styles.row}>
        <label>
          <input
            type="radio"
            checked={form.type === "Tow"}
            onChange={() => setForm({ ...form, type: "Tow" })}
          />
          Tow
        </label>
        <label>
          <input
            type="radio"
            checked={form.type === "Clamp"}
            onChange={() => setForm({ ...form, type: "Clamp" })}
          />
          Clamp
        </label>
      </div>

      {/* Inputs */}
      <select
        style={styles.input}
        value={form.vehicleType}
        onChange={(e) =>
          setForm({ ...form, vehicleType: e.target.value })
        }
      >
        <option>2W</option>
        <option>3W</option>
        <option>4W</option>
        <option>Bus/Truck</option>
      </select>

      <input
        placeholder="Vehicle Number"
        style={styles.input}
        value={form.vehicleNumber}
        onChange={(e) =>
          setForm({ ...form, vehicleNumber: e.target.value.toUpperCase() })
        }
      />

      <select
        style={styles.input}
        onChange={(e) =>
          setForm({ ...form, reason: e.target.value })
        }
      >
        <option value="">Select Reason</option>
        <option>No Parking</option>
        <option>Double Parking</option>
        <option>Blocking Traffic</option>
      </select>

      <input
        placeholder="Yard"
        style={styles.input}
        onChange={(e) =>
          setForm({ ...form, yard: e.target.value })
        }
      />

      <input
        type="number"
        placeholder="Amount"
        style={styles.input}
        onChange={(e) =>
          setForm({ ...form, amount: e.target.value })
        }
      />

      {/* 📷 Vehicle Photos */}
      <div style={styles.uploadCard}>
        <p>Vehicle Photos</p>

        <button
          style={styles.cameraBtn}
          onClick={() => captureImage("vehicleImages", true)}
        >
          <Camera size={18} /> Capture Photo
        </button>

        <div style={styles.previewRow}>
          {form.vehicleImages.map((img, i) => (
            <img key={i} src={img} style={styles.preview} />
          ))}
        </div>
      </div>

      {/* 📷 Number Plate */}
      <div style={styles.uploadCard}>
        <p>Number Plate</p>

        <button
          style={styles.cameraBtn}
          onClick={() => captureImage("numberPlateImage")}
        >
          <Camera size={18} /> Capture Plate
        </button>

        {form.numberPlateImage && (
          <img src={form.numberPlateImage} style={styles.preview} />
        )}
      </div>


      {/* Submit */}
      <button
        style={styles.submit}
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? "Saving..." : "Submit Entry"}
      </button>
    </div>
  );
};

const styles = {
  container: {
    padding: "15px",
    paddingBottom: "80px",
  },
  input: {
    width: "100%",
    padding: "12px",
    marginBottom: "10px",
    borderRadius: "10px",
    border: "1px solid #ddd",
  },
  row: {
    display: "flex",
    gap: "15px",
    marginBottom: "10px",
  },
  uploadCard: {
    background: "#fff",
    padding: "12px",
    borderRadius: "12px",
    marginBottom: "10px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  cameraBtn: {
    width: "100%",
    padding: "10px",
    background: "#3b82f6",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    marginTop: "5px",
  },
  previewRow: {
    display: "flex",
    gap: "5px",
    marginTop: "10px",
  },
  preview: {
    width: "70px",
    height: "70px",
    borderRadius: "8px",
    objectFit: "cover",
  },
  secondary: {
    width: "100%",
    padding: "12px",
    background: "#64748b",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    marginBottom: "10px",
  },
  submit: {
    width: "100%",
    padding: "14px",
    background: "#33AE54",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontWeight: "bold",
  },
};

export default TowClampCreate;